import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { navigate } from '@reach/router';
import BookAudioPlayer from '../modules/books-page/bookAudioPlayer';
import MobileBookAudioPlayer from '../modules/books-page/mobileBookAudioPlayer';
import useBreakpoint from '../hooks/useBreakpoint';
import Portal from '../components/Portal';
import { getStudyChapterNavigatePath, isSpanishGcPathname } from '../constants/spanish-gc';

const BookAudioPlayerContext = createContext();

export const BookAudioPlayerProvider = ({ children }) => {
  const [audioDetails, setAudioDetails] = useState(undefined);
  const [audioState, setAudioState] = useState('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0);
  const [totalDurations, setTotalDurations] = useState('');
  const [rawTotalDuration, setRawTotalDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentAudioId, setCurrentAudioId] = useState(null);
  const [volume, setVolume] = useState(0.5);
  const [selectedVoice, setSelectedVoice] = useState('Charlene');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [chaptersQueue, setChaptersQueue] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(null);
  const [narrationStartTime, setNarrationStartTime] = useState(0);
  const [narrationStartReady, setNarrationStartReady] = useState(true);

  const audioRef = useRef(null);
  const analyzerRef = useRef(null);
  const silenceDetectionIntervalRef = useRef(null);
  const corsAllowedRef = useRef(false);
  const narrationStartCacheRef = useRef({});
  const currentDetectionAudioIdRef = useRef(null);
  const speakingTimeRef = useRef(0);
  const silentTimeRef = useRef(0);
  const lastVadTimeRef = useRef(0);
  const userSeekCounterRef = useRef(0);

  const getSelectedAudioId = (voiceName, details) => {
    if (voiceName === 'Charlene') return details?.audioByCharleneId;
    if (voiceName === 'Callie') return details?.audioByCallieId;
    if (voiceName === 'Nancy') return details?.audioByNancyId;
    return details?.audioByCharleneId;
  };

  const probeCors = async (url) => {
    try {
      const res = await fetch(url, { method: 'HEAD', mode: 'cors' });
      return res.ok;
    } catch {
      return false;
    }
  };

  const setupAnalyzer = () => {
    if (analyzerRef.current) return analyzerRef.current;
    if (!audioRef.current) return null;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      const audioContext = new AudioCtx();
      const source = audioContext.createMediaElementSource(audioRef.current);
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 2048;
      source.connect(analyzer);
      analyzer.connect(audioContext.destination);
      analyzerRef.current = { audioContext, analyzer };
      return analyzerRef.current;
    } catch (err) {
      console.warn('[ReadAlong] Audio analyzer setup failed:', err.message);
      return null;
    }
  };

  const stopSilenceDetection = () => {
    if (silenceDetectionIntervalRef.current) {
      clearInterval(silenceDetectionIntervalRef.current);
      silenceDetectionIntervalRef.current = null;
    }
    currentDetectionAudioIdRef.current = null;
  };

  const startSilenceDetection = (audioId) => {
    stopSilenceDetection();
    if (!audioId) return;

    speakingTimeRef.current = 0;
    silentTimeRef.current = 0;
    lastVadTimeRef.current = 0;

    let narrationDetected = false;
    if (audioId in narrationStartCacheRef.current) {
      setNarrationStartTime(narrationStartCacheRef.current[audioId]);
      setNarrationStartReady(true);
      narrationDetected = true;
    }

    if (!corsAllowedRef.current) {
      if (!narrationDetected) {
        setNarrationStartTime(0);
        setNarrationStartReady(true);
      }
      return;
    }

    const setup = setupAnalyzer();
    if (!setup) {
      if (!narrationDetected) {
        setNarrationStartTime(0);
        setNarrationStartReady(true);
      }
      return;
    }

    const { audioContext, analyzer } = setup;
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    currentDetectionAudioIdRef.current = audioId;
    const buffer = new Uint8Array(analyzer.fftSize);
    const SILENCE_THRESHOLD = 0.02;
    const VAD_POLL_MS = 100;
    const MAX_DT = 0.5;
    const startedAt = Date.now();
    const MAX_DETECTION_MS = 30000;

    silenceDetectionIntervalRef.current = setInterval(() => {
      if (currentDetectionAudioIdRef.current !== audioId) {
        stopSilenceDetection();
        return;
      }
      if (!audioRef.current) return;
      if (audioRef.current.paused || audioRef.current.seeking) {
        lastVadTimeRef.current = audioRef.current.currentTime;
        return;
      }

      analyzer.getByteTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        const normalized = (buffer[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / buffer.length);
      const isSpeaking = rms > SILENCE_THRESHOLD;
      const now = audioRef.current.currentTime;

      if (!narrationDetected) {
        if (isSpeaking) {
          narrationStartCacheRef.current[audioId] = now;
          setNarrationStartTime(now);
          setNarrationStartReady(true);
          narrationDetected = true;
          lastVadTimeRef.current = now;
        } else if (Date.now() - startedAt > MAX_DETECTION_MS) {
          narrationStartCacheRef.current[audioId] = 0;
          setNarrationStartTime(0);
          setNarrationStartReady(true);
          narrationDetected = true;
          lastVadTimeRef.current = now;
        }
        return;
      }

      const dt = now - lastVadTimeRef.current;
      lastVadTimeRef.current = now;
      if (dt <= 0 || dt > MAX_DT) return;

      if (isSpeaking) {
        speakingTimeRef.current += dt;
      } else {
        silentTimeRef.current += dt;
      }
    }, VAD_POLL_MS);
  };

  const fetchAudioAndPlay = async (audioId) => {
    if (!audioId) {
      console.error('No audio ID provided');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${process.env.GATSBY_LINEAGE_BACKEND_URL}api/podcasts/${audioId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      const data = await response.text();
      const { payload } = JSON.parse(data);

      if (payload?.audioUrl) {
        setCurrentAudioId(audioId);

        stopSilenceDetection();
        const cached = narrationStartCacheRef.current[audioId];
        if (cached !== undefined) {
          setNarrationStartTime(cached);
          setNarrationStartReady(true);
          corsAllowedRef.current = true;
        } else {
          const corsAllowed = await probeCors(payload.audioUrl);
          corsAllowedRef.current = corsAllowed;
          if (audioRef.current) {
            if (corsAllowed) {
              audioRef.current.crossOrigin = 'anonymous';
              setNarrationStartReady(false);
              setNarrationStartTime(0);
            } else {
              audioRef.current.removeAttribute('crossorigin');
              setNarrationStartReady(true);
              setNarrationStartTime(0);
            }
          }
        }

        play(payload?.audioUrl);

        if (corsAllowedRef.current && !(audioId in narrationStartCacheRef.current)) {
          startSilenceDetection(audioId);
        }
      } else {
        throw new Error('No audio URL found in response');
      }
    } catch (err) {
      console.error('Error fetching audio:', err);
      setError(err.message);
      setAudioState('idle');
    } finally {
      setIsLoading(false);
    }
  };

  const play = (audioUrl) => {
    if (!audioRef.current) return;

    if (audioUrl) {
      audioRef.current.src = audioUrl;
      setProgressPercent(0);
      setCurrentTime('00:00');
      updateAudioPlayerState('playing');
    }

    audioRef.current.playbackRate = playbackSpeed;

    audioRef.current
      .play()
      .then(() => {
        setAudioState('playing');
      })
      .catch((err) => {
        console.error('Error playing audio:', err);
        setError('Failed to play audio');
      });
  };

  const pause = () => {
    if (!audioRef.current) return;
    updateAudioPlayerState('paused');
    audioRef.current.pause();
    setAudioState('paused');
  };

  const toggle = (newAudioDetails) => {
    const selectedAudioId = getSelectedAudioId(selectedVoice, newAudioDetails);

    const isDifferentAudio =
      !audioDetails ||
      audioDetails.audioByCharleneId !== newAudioDetails?.audioByCharleneId ||
      audioDetails.audioByCallieId !== newAudioDetails?.audioByCallieId ||
      audioDetails.audioByNancyId !== newAudioDetails?.audioByNancyId;

    const isDifferentAudioId = currentAudioId !== selectedAudioId;

    if (isDifferentAudio || isDifferentAudioId) {
      setAudioDetails(newAudioDetails);
      setCurrentChapterIndex(newAudioDetails?.index);
      fetchAudioAndPlay(selectedAudioId);
    } else {
      if (audioState === 'playing') {
        pause();
      } else if (audioState === 'paused' || audioState === 'idle') {
        if (audioRef.current && audioRef.current.src) {
          play();
        } else {
          fetchAudioAndPlay(selectedAudioId);
        }
      }
    }
  };

  const handleSeek = (targetDuration) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = targetDuration;
    userSeekCounterRef.current++;
    // Reset VAD so the fallback formula re-anchors from the new position
    speakingTimeRef.current = 0;
    silentTimeRef.current = 0;
  };

  const handleUpdateVolume = (value) => {
    if (!audioRef.current) return;
    const clampedValue = Math.max(0, Math.min(1, value));
    setVolume(clampedValue);
    audioRef.current.volume = clampedValue;
  };

  const handlePlaybackSpeedChange = (speed) => {
    const speedValue = parseFloat(speed.replace('x', ''));
    setPlaybackSpeed(speedValue);
    if (audioRef.current) {
      audioRef.current.playbackRate = speedValue;
    }
  };

  const getQueueChapterPath = (uid) => {
    const isSpanishContext =
      typeof window !== 'undefined' && isSpanishGcPathname(window.location.pathname);
    return getStudyChapterNavigatePath(uid, isSpanishContext);
  };

  const handleNext = () => {
    if (!chaptersQueue?.length || currentChapterIndex === null) return;

    const nextIndex = currentChapterIndex === chaptersQueue.length - 1 ? 0 : currentChapterIndex + 1;
    const nextChapter = chaptersQueue[nextIndex];

    if (nextChapter) {
      navigate(getQueueChapterPath(nextChapter.uid));
      const nextAudioDetails = {
        audioByCharleneId: nextChapter.audioByCharleneId,
        audioByCallieId: nextChapter.audioByCallieId,
        audioByNancyId: nextChapter.audioByNancyId,
        audioTitle: nextChapter.audioTitle,
        index: nextIndex,
      };
      setAudioDetails(nextAudioDetails);
      setCurrentChapterIndex(nextIndex);
      fetchAudioAndPlay(getSelectedAudioId(selectedVoice, nextChapter));
    }
  };

  const handlePrevious = () => {
    if (!chaptersQueue?.length || currentChapterIndex === null) return;

    const prevIndex = currentChapterIndex === 0 ? chaptersQueue.length - 1 : currentChapterIndex - 1;
    const prevChapter = chaptersQueue[prevIndex];

    if (prevChapter) {
      navigate(getQueueChapterPath(prevChapter.uid));
      const prevAudioDetails = {
        audioByCharleneId: prevChapter.audioByCharleneId,
        audioByCallieId: prevChapter.audioByCallieId,
        audioByNancyId: prevChapter.audioByNancyId,
        audioTitle: prevChapter.audioTitle,
        index: prevIndex,
      };
      setAudioDetails(prevAudioDetails);
      setCurrentChapterIndex(prevIndex);
      fetchAudioAndPlay(getSelectedAudioId(selectedVoice, prevChapter));
    }
  };

  const stop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setAudioState('idle');
    setProgressPercent(0);
    setCurrentTime('00:00');
  };

  const handleClose = () => {
    stop();
    setAudioDetails(null);
    setCurrentAudioId(null);
    setCurrentChapterIndex(null);
    updateAudioPlayerState('idle');
    stopSilenceDetection();
    setNarrationStartTime(0);
    setNarrationStartReady(true);
  };

  useEffect(() => {
    return () => stopSilenceDetection();
  }, []);

  // Switch audio track when voice changes mid-playback
  useEffect(() => {
    if (audioDetails && audioState === 'playing') {
      const newAudioId = getSelectedAudioId(selectedVoice, audioDetails);
      if (newAudioId !== currentAudioId) {
        fetchAudioAndPlay(newAudioId);
      }
    }
  }, [selectedVoice]);

  const breakpoint = useBreakpoint();

  return (
    <Portal>
      <BookAudioPlayerContext.Provider
        value={{
          toggle,
          stop,
          audioDetails,
          handlePrevious,
          handleNext,
          handleClose,
          handleUpdateVolume,
          audioState,
          progressPercent,
          currentTime,
          totalDurations,
          handleSeek,
          volume,
          rawTotalDuration,
          setVolume,
          selectedVoice,
          setSelectedVoice,
          fetchAudioAndPlay,
          isLoading,
          error,
          currentAudioId,
          playbackSpeed,
          currentTimeInSeconds,
          handlePlaybackSpeedChange,
          chaptersQueue,
          setChaptersQueue,
          currentChapterIndex,
          setCurrentChapterIndex,
          narrationStartTime,
          narrationStartReady,
          speakingTimeRef,
          silentTimeRef,
          userSeekCounterRef,
          audioRef,
        }}
      >
        {children}
        <audio
          controls
          onLoadedMetadata={(e) => {
            const duration = e.currentTarget.duration;
            if (!isNaN(duration)) {
              const minutes = Math.floor(duration / 60);
              const seconds = Math.floor(duration % 60);
              setTotalDurations(`${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
              setRawTotalDuration(duration);
            }
            e.currentTarget.playbackRate = playbackSpeed;
          }}
          onTimeUpdate={(e) => {
            const audio = e.currentTarget;
            if (!audio) return;
            const t = audio.currentTime;
            const mins = Math.floor(t / 60);
            const secs = Math.floor(t % 60);
            setCurrentTimeInSeconds(t);
            setCurrentTime(`${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`);
            setProgressPercent((t / audio.duration) * 100);
          }}
          onEnded={handleNext}
          onError={(e) => {
            console.error('Audio error:', e);
            setError('Failed to load audio');
            setAudioState('idle');
          }}
          ref={audioRef}
          style={{ position: 'fixed', pointerEvents: 'none', opacity: 0, left: '-100%' }}
        />
        {audioDetails && (
          <div className="wrapper wrapper-xl ">
            <div className="wrapper wrapper-xl pd-x-md pd-x-sm book-audio-player-container">
              {breakpoint.mobileAndTablet ? <MobileBookAudioPlayer /> : <BookAudioPlayer />}
            </div>
          </div>
        )}
      </BookAudioPlayerContext.Provider>
    </Portal>
  );
};

const updateAudioPlayerState = (audioState) => {
  if (typeof window !== undefined) {
    document.body.setAttribute('data-audio-state', audioState);
  }
};

export const useBookAudioPlayer = () => {
  const context = useContext(BookAudioPlayerContext);
  if (!context) {
    throw new Error('useBookAudioPlayer must be used within BookAudioPlayerProvider');
  }
  return context;
};
